import { Badge, type DataTableColumn } from "@design";
import { useTranslation } from "react-i18next";
import type { OAuthClientResponse } from "@/api";
import { CopyValue } from "@/components/CopyValue";
import { Text } from "@/components/Text";
import { formatDate } from "@/i18n/format";

function NameCell({ client }: Readonly<{ client: OAuthClientResponse }>) {
  const { t } = useTranslation("adminPlatform");
  const type = client.clientType === "confidential" ? "confidential" : "public";
  return (
    <div className="flex min-w-0 flex-col gap-1 text-left">
      <Text as="span" variant="body-sm" className="truncate">
        {client.clientName}
      </Text>
      <span className="flex flex-wrap gap-1">
        <Badge size="compact" variant="neutral">
          {t(`oauthClients.types.${type}`)}
        </Badge>
        {client.isFirstParty && (
          <Badge size="compact" variant="info">
            {t("oauthClients.firstParty")}
          </Badge>
        )}
      </span>
    </div>
  );
}

function AccessCell({ client }: Readonly<{ client: OAuthClientResponse }>) {
  const { t } = useTranslation("adminPlatform");
  const scopes = client.allowedScopes ?? [];
  return (
    <div className="flex min-w-0 flex-col items-end md:items-start">
      <Text as="span" variant="body-sm" tone="secondary">
        {t("oauthClients.redirectCount", { count: (client.redirectUris ?? []).length })}
      </Text>
      <Text as="span" variant="caption" tone="tertiary" className="max-w-64 truncate">
        {scopes.length > 0 ? scopes.join(" ") : t("oauthClients.none")}
      </Text>
    </div>
  );
}

export function useOAuthClientColumns(): DataTableColumn<OAuthClientResponse>[] {
  const { t } = useTranslation("adminPlatform");
  return [
    {
      key: "name",
      header: t("oauthClients.columns.name"),
      mobile: "primary",
      render: (client) => <NameCell client={client} />,
    },
    {
      key: "clientId",
      header: t("oauthClients.columns.clientId"),
      interactive: true,
      render: (client) => (
        <div className="max-w-56">
          <CopyValue value={client.clientId} label={t("oauthClients.columns.clientId")} />
        </div>
      ),
    },
    { key: "access", header: t("oauthClients.columns.access"), render: (client) => <AccessCell client={client} /> },
    {
      key: "status",
      header: t("oauthClients.columns.status"),
      render: (client) => (
        <Badge variant={client.isEnabled ? "success" : "neutral"}>
          {t(client.isEnabled ? "oauthClients.status.enabled" : "oauthClients.status.disabled")}
        </Badge>
      ),
    },
    {
      key: "created",
      header: t("oauthClients.columns.created"),
      render: (client) => (
        <Text as="span" variant="body-sm" tone="tertiary">
          {client.createdAt ? formatDate(client.createdAt) : t("oauthClients.none")}
        </Text>
      ),
    },
  ];
}
